// Presentation-only estimates. The server remains authoritative for orders and balances.
const nonNegative = (value, {integer=false}={}) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !/^\d+(?:\.\d+)?$/.test(value.trim())) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= Number.MAX_SAFE_INTEGER && (!integer || Number.isSafeInteger(number)) ? number : null;
};

const safeRialSum = values => {
  if (!values.every(value => Number.isSafeInteger(value) && value >= 0)) return null;
  const total = values.reduce((sum,value)=>sum+value,0);
  return Number.isSafeInteger(total) ? total : null;
};

function metalValue(weight, unitPrice) {
  const grams = nonNegative(weight);
  if (grams === null) return null;
  if (grams === 0) return 0;
  const rate = nonNegative(unitPrice,{integer:true});
  const estimate = rate !== null && rate > 0 ? Math.round(grams * rate) : null;
  return Number.isSafeInteger(estimate) ? estimate : null;
}

/** Native rial throughout; callers must convert a gold source in toman exactly once. */
export function estimatePortfolio({ silverGrams, goldGrams, barHoldings, silverBuyRial, goldMarketRial, walletRial, copperGrams=0, copperBuyRial=null }) {
  const bars = Array.isArray(barHoldings) ? barHoldings : null;
  const weights = bars?.map(holding => nonNegative(holding?.total_gram));
  const barGrams = nonNegative(weights && weights.every(weight => weight !== null)
    ? weights.reduce((total, weight) => total + weight, 0)
    : null);
  const values = {
    copperRial: metalValue(copperGrams,copperBuyRial),
    silverRial: metalValue(silverGrams, silverBuyRial),
    goldRial: metalValue(goldGrams, goldMarketRial),
    barsRial: metalValue(barGrams, silverBuyRial),
    walletRial: nonNegative(walletRial,{integer:true}),
  };
  const metalValues = [values.silverRial, values.goldRial, values.barsRial, values.copperRial];
  const metalRial = safeRialSum(metalValues);
  return {
    ...values,
    barGrams,
    metalRial,
    totalRial: safeRialSum([metalRial,values.walletRial]),
    // Matches existing goal semantics: rial wallet and silver pellets only.
    goalBasisRial: safeRialSum([values.silverRial,values.walletRial]),
  };
}

export function goalProgress(targetRial, basisRial) {
  const target = nonNegative(targetRial,{integer:true});
  const basis = nonNegative(basisRial,{integer:true});
  if (!target || basis === null) return null;
  return Math.min(100, Math.round((basis / target) * 1000) / 10);
}
